import { type FC } from "react";
import { Stack } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { FilterProvider, useFilter } from "@/partner/shared/components/Filter";
import { PromocodesFilter } from "./PromocodesFilter";
import { PromocodesList } from "./PromocodesList";
import { CreatePromocodeDialog } from "./CreatePromocodeDialog";
import { CreateMultiplePromocodesDialog } from "./CreateMultiplePromocodesDialog";
import { EditPromocodeDialog } from "./EditPromocodeDialog";

// Вкладки-диалоги в URL (?tab=), паттерн products/streams; значения — из
// référence Promocodes.js (create / createMany / edit&id).
const PromocodesInner: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { onChangeParams } = useFilter();

  const tab = searchParams.get("tab");
  const editId = searchParams.get("id");

  const handleCreate = () => {
    searchParams.set("tab", "create");
    setSearchParams(searchParams);
  };

  const handleCreateMultiple = () => {
    searchParams.set("tab", "createMany");
    setSearchParams(searchParams);
  };

  const handleEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  const handleClose = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };

  // Référence: после масс-генерации — закрыть диалог и отфильтровать список
  // по только что созданной кампании. Оба изменения (снятие tab/id и
  // установка фильтра) должны уйти ОДНИМ setSearchParams — иначе вторая
  // навигация (через другой инстанс searchParams, живущий в FilterProvider)
  // перезатирает первую и возвращает tab=createMany в URL (диалог не
  // закрывается). onChangeParams бьёт по единственному инстансу
  // searchParams, которым владеет FilterProvider, поэтому используем только
  // его — без отдельного handleClose().
  const handleMultipleCreated = (campaignId: string | null) => {
    onChangeParams({
      tab: null,
      id: null,
      "filter[promocodeCampaignIds]": campaignId ? [campaignId] : null,
    });
  };

  return (
    <>
      <Stack spacing={1}>
        <PromocodesFilter
          onCreate={handleCreate}
          onCreateMultiple={handleCreateMultiple}
        />
        <PromocodesList onEdit={handleEdit} />
      </Stack>

      <CreateMultiplePromocodesDialog
        open={tab === "createMany"}
        onClose={handleClose}
        onCompleted={handleMultipleCreated}
      />
      <CreatePromocodeDialog
        open={tab === "create"}
        onClose={handleClose}
        onCompleted={handleClose}
      />
      <EditPromocodeDialog
        open={tab === "edit" && Boolean(editId)}
        promocodeId={editId}
        onClose={handleClose}
        onCompleted={handleClose}
      />
    </>
  );
};

export const Promocodes: FC = () => (
  <FilterProvider>
    <PromocodesInner />
  </FilterProvider>
);
